import { Badge, Box, FlexBox } from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { textualChannels } from 'lib-shared-types-channels';
import { ComponentProps, memo, useContext, useMemo } from 'react';

import { ConversationContext } from 'components/conversation';
import {
  CopilotAction,
  CopilotDropdown,
  CopilotDropdownActionParams,
} from 'components/copilotDropdown';
import { useCopilotTrial } from 'utils/useCopilotTrial';

import { ConversationCopilotDropdownTranslate } from './components/translate/conversationCopilotDropdownTranslate';

type ConversationCopilotDropdownProps = {
  buttonProps?: ComponentProps<typeof CopilotDropdown>['buttonProps'];
};

export type ConversationCopilotActions = {
  translate: CopilotAction;
  toggleSummary: CopilotAction;
  goToCopilotSettings: CopilotAction;
};

/**
 * Represents a dropdown component designed to be integrated into a rich text
 * editor's toolbar as an addon, providing AI-powered functionalities to enhance
 * and refine the content within the editor.
 */
export const ConversationCopilotDropdown = memo(
  function ConversationCopilotDropdown(
    props: ConversationCopilotDropdownProps,
  ): JSX.Element | null {
    const { hasCopilot } = useCopilotTrial();
    const conversationContext = useContext(ConversationContext);

    const conversationCopilotActions: ConversationCopilotActions =
      useMemo(() => {
        const {
          conversation,
          isAutoSummarizing,
          isAutoTranslating,
          isSummaryVisible,
          translationLanguageCode,
        } = conversationContext;

        return {
          translate: {
            title: () => {
              return (
                <FlexBox>
                  {translationLanguageCode
                    ? 'Change translation language'
                    : 'Translate'}
                  {isAutoTranslating && (
                    <Box margin="0 0 0 200">
                      <Badge variant="success" size="small">
                        Auto
                      </Badge>
                    </Box>
                  )}
                </FlexBox>
              );
            },
            icon: 'translate',
            commandName: 'conversation:translate',
            isVisible: ({ hasCopilot }) => hasCopilot,
            isDisabled: ({}: CopilotDropdownActionParams): boolean => {
              if (
                textualChannels.some(
                  (channel) =>
                    channel ===
                    convertChannelCasing(
                      conversation?.initial_channel ?? '',
                      'snake',
                    ),
                )
              ) {
                return false;
              }

              return true;
            },
            content: <ConversationCopilotDropdownTranslate />,
          },

          toggleSummary: {
            title: () => {
              return (
                <FlexBox>
                  {isSummaryVisible ? 'Hide summary' : 'Show summary'}
                  {isAutoSummarizing && (
                    <Box margin="0 0 0 200">
                      <Badge variant="success" size="small">
                        Auto
                      </Badge>
                    </Box>
                  )}
                </FlexBox>
              );
            },
            commandName: 'conversation:summary',
            icon: 'list-ul',
            isVisible: ({ hasCopilot }) => hasCopilot,
            isDisabled: () =>
              !conversationContext?.conversation?.summary?.can_be_generated,
            action: ({ dropdownContext }) => {
              dropdownContext?.closeDropdown();
              conversationContext?.setIsSummaryVisible?.(
                !conversationContext?.isSummaryVisible,
              );
            },
          },
          goToCopilotSettings: {
            title: () => 'Go to Copilot settings',
            icon: 'cog',
            isVisible: ({ featureFlags }) => {
              return featureFlags?.agentCopilotAutoTranslate ?? false;
            },
            action: ({ dropdownContext, navigate }) => {
              dropdownContext?.closeDropdown();
              navigate!({ path: '/settings/preferences/agent-copilot' });
            },
            hasDividerBefore: true,
          },
        };
      }, [conversationContext]);

    if (!hasCopilot) {
      return null;
    }

    return (
      <CopilotDropdown
        buttonProps={props.buttonProps}
        icon="ai"
        fixed
        actions={conversationCopilotActions}
      />
    );
  },
);
